// Browse location state — "where am I, which dishes am I looking at, how far
// away are they."
//
// This is ENTIRELY SEPARATE from the user's saved PROFILE (home) address.
// `location` here is the browse reference: the operating locale + the coords the
// feed/distance are computed against, captured from device geolocation or a
// typed ZIP. It is NEVER mirrored to, nor hydrated from, the profile address.
// The profile home address is a distinct concept (orders/checkout/publish) set
// only where the user explicitly enters it; see `useAddressCapture`
// (`mirrorMode: 'profileAddress'`) and the order gate.
//
// A location is no longer required to use the app — every user lands on the
// home feed, which shows an inline capture prompt (InlineLocationPrompt) when
// no sufficient location is set. The value lives in localStorage only (same for
// anonymous and auth users). On login we restore only the operating *locale*
// from `profile.currentLocaleId` — never the address.

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { Address } from '../types/address';
import type { Locale } from '../services/localeService';

/**
 * How the current `location` (Address) was obtained. ONLY `'device'`
 * (browser geolocation) is allowed to expose distance / drive-time
 * calculations to the UI — see the data-layer strip in `useListings` /
 * `useOrders` and the gate in `DishInfoCard`. A ZIP / centroid (`'postal'`)
 * is too coarse to promise an accurate distance, so distance is hidden.
 * `null` means we have no user-supplied coords at all.
 */
export type LocationSource = 'device' | 'postal';

interface LocationState {
  location: Address | null;
  localeId: string | null;       // mirror of locale.id, kept for the many existing consumers
  locale: Locale | null;         // full resolved locale (dish or postalCode), source of truth for display name
  // Provenance of `location`. Set by the capture flows; the single signal that
  // unlocks distance display. Persisted so a device-located returning user
  // keeps distances without re-prompting (their cached coords are device-precise).
  locationSource: LocationSource | null;
  // Epoch-ms of the last `'device'` fix. Drives the app-open / tab-focus
  // refresh throttle in DeviceLocationGuard (re-read at most once per TTL) and
  // is persisted so a reload inside the window doesn't re-poll. Null when the
  // current location isn't a device fix.
  deviceFixAt: number | null;
  // The ZIP the user typed in the TopBar switcher, kept for display only
  // ("Showing 80202"). Set alongside `locationSource === 'postal'`; cleared on
  // any device capture or non-typed resolve. NOT a second provenance flag —
  // `locationSource` is the single source of truth for device-vs-ZIP.
  //
  // Null when the user searched by city rather than by ZIP: there is no ZIP to
  // echo back, and the locale's own name ("Denver, CO") is the better label
  // anyway, so the switcher falls back to a source-agnostic line.
  localeZip: string | null;
  setLocation: (address: Address, source?: LocationSource | null) => void;
  setLocaleId: (id: string | null) => void;
  setLocale: (locale: Locale | null) => void;
  // TopBar locale switch from a typed search: set the operating locale and
  // stamp `locationSource = 'postal'` — the single provenance flag — so distance
  // and the crosshair stay off and the feed anchors on the locale centroid.
  // Leaves the saved `location` Address (the user's real street address) untouched.
  // `zip` is the ZIP the user typed, or null when they searched by city name.
  setLocaleFromSearch: (locale: Locale | null, zip: string | null) => void;
  // Adjust provenance without touching coords/locale. Used by the device-
  // location guard to downgrade off 'device' when geolocation permission is
  // revoked (distance hides, but the cached coords still drive the feed).
  setLocationSource: (source: LocationSource | null) => void;
  clearLocation: () => void;
}

/**
 * Field-pickers for the `partialize` write boundary. In-memory state stays
 * fat (Address / Locale carry whatever the geocoder / BE returned) so all
 * existing consumers keep working without churn. localStorage only sees
 * the projection below — every field listed has a verified consumer, with
 * the reason in-line for the next sweep.
 *
 * Drops vs. fat shape:
 *   - Address: `address` (formatted display string), `county`, `countyLong`,
 *     `stateProvinceLong`, `countryLong` — never read off the cached value.
 *   - Locale: BaseModel cruft (`createdAt`, `updatedAt`, `deletedAt`,
 *     `instanceVersion`, `createdBy`, `updatedBy`, `metadata`),
 *     `langCache` (BE-only), and `city`/`country`/`stateProvince`/
 *     `postalCode` (display falls back to the location, not the locale).
 *
 * undefined fields are passed through; JSON.stringify drops them at the
 * persist boundary so the on-disk JSON stays minimal.
 */
function slimLocation(addr: Address | null | undefined): Address | null {
  if (!addr) return null;
  return {
    street1: addr.street1,             // authService mirror to profile
    street2: addr.street2,             // authService mirror to profile
    city: addr.city,                   // TopBar display fallback
    stateProvince: addr.stateProvince, // TopBar display fallback, mirror
    postalCode: addr.postalCode,       // gate (isSufficientLocation), localeInit query, mirror
    country: addr.country,             // gate (isSufficientLocation), mirror
    lat: addr.lat,                     // HomePage listings query, localeInit query, mirror
    lon: addr.lon,                     // HomePage listings query, localeInit query, mirror
    type: addr.type,                   // marks `type='profile'` addresses
    noUnit: addr.noUnit,               // user-confirmed "no apt" — prompt suppression across sessions
    pinConfirmed: addr.pinConfirmed,   // user-confirmed pin-only location — same suppression
    placeId: addr.placeId,             // AddressEditCard round-trip — skips re-geocode
    geocoded: addr.geocoded,           // AddressEditCard round-trip
  };
}

function slimLocale(loc: Locale | null | undefined): Locale | null {
  if (!loc) return null;
  return {
    id: loc.id,                                            // wsService subscribe, listings query
    type: loc.type,                                        // HomePage isPostalOnly check
    name: loc.name,                                        // TopBar display label
    lat: loc.lat,                                          // useMaps centroid
    lon: loc.lon,                                          // useMaps centroid
    radius: loc.radius,                                    // useMaps radius
    maxShareConnectionLevel: loc.maxShareConnectionLevel,  // ProfilePage cap, localeInit stale-shape check
  };
}

export const useLocationStore = create<LocationState>()(
  persist(
    (set) => ({
      location: null,
      localeId: null,
      locale: null,
      locationSource: null,
      deviceFixAt: null,
      localeZip: null,
      // Setting a new location invalidates any previously-resolved locale so
      // InitLocale re-queries GET /locale (a dish locale may now match where
      // it previously didn't, and vice versa). `source` defaults to null:
      // only the device-geolocation capture path passes `'device'`, so any
      // other write (manual edit, ZIP) drops distance exposure. Locale
      // provenance is cleared too — the about-to-be-re-resolved locale is no
      // longer the one the user typed a ZIP for.
      setLocation: (location, source = null) =>
        set({
          location,
          localeId: null,
          locale: null,
          locationSource: source,
          // Stamp the fix time only for device captures — that's the timestamp
          // the refresh throttle reads. Non-device writes clear it.
          deviceFixAt: source === 'device' ? Date.now() : null,
          // A fresh precise capture is no longer "a ZIP the user typed".
          localeZip: null,
        }),
      setLocaleId: (localeId) => set({ localeId }),
      // Locale-only update (InitLocale auto-resolve / login mirror) leaves
      // `locationSource` untouched — it doesn't change the user's coords — and
      // clears the typed-ZIP label: this wasn't a user-typed switch.
      setLocale: (locale) => set({ locale, localeId: locale?.id ?? null, localeZip: null }),
      setLocaleFromSearch: (locale, zip) =>
        set({
          locale,
          localeId: locale?.id ?? null,
          localeZip: zip,
          // The single provenance flag. 'postal' hides distance + crosshair and
          // points the feed at the centroid; it does NOT touch `location`.
          locationSource: 'postal',
          deviceFixAt: null,
        }),
      setLocationSource: (locationSource) => set({ locationSource }),
      clearLocation: () =>
        set({ location: null, localeId: null, locale: null, locationSource: null, deviceFixAt: null, localeZip: null }),
    }),
    {
      name: 'hp-location',
      // Bump when the cached `Locale` shape evolves OR the BE's auto-create
      // rules change in a way that makes the cached value mean something
      // different.
      //   v2: city/state dedup + Colorado postalCode→dish promotion.
      //   v3: dropped audit / unused fields from the persisted projection
      //       (see `slimLocation` / `slimLocale` for the kept set). Migrate
      //       re-projects already-cached values so the BE cruft is evicted
      //       on next load even from users that don't re-resolve.
      //   v4: added `locationSource`. Pre-v4 caches have no provenance, so we
      //       default it to null — distance stays hidden until the user
      //       re-captures via device geolocation. Conservative by design.
      //   v5: added `localeSource` / `localeZip` (TopBar switcher provenance).
      //       Pre-v5 caches default both to null — the switcher shows the
      //       locale name only until the next user-typed switch.
      //   v6: added `deviceFixAt` (refresh-throttle timestamp). Pre-v6 caches
      //       default to null → treated as maximally stale, so a device user
      //       gets one fresh fix on their next app-open.
      //   v7: collapsed `localeSource` into the single `locationSource` (one
      //       source of truth for device-vs-ZIP). Pre-v7 caches could carry a
      //       ZIP choice only on localeSource ('postal') while locationSource
      //       still read 'device' — the divergence that showed distances on a
      //       ZIP. Carry a typed-ZIP choice onto the unified flag, then drop
      //       localeSource.
      version: 7,
      migrate: (persistedState, version) => {
        let state = persistedState as {
          location?: Address | null;
          locale?: Locale | null;
          locationSource?: LocationSource | null;
          deviceFixAt?: number | null;
          localeSource?: LocationSource | null;
          localeZip?: string | null;
        } | null;
        if (state && typeof state === 'object') {
          if (version < 2) {
            // v2 step: drop cached locale so InitLocale re-resolves with
            // the new auto-create rules. Address stays so the gate doesn't
            // re-prompt.
            state = { ...state, locale: null };
          }
          if (version < 3) {
            // v3 step: shrink to the persisted field set. Idempotent — a
            // pre-v3 fat blob comes in, a v3 slim blob goes out; the BE
            // audit fields and unused display fields disappear without
            // disturbing any consumer.
            state = {
              ...state,
              location: slimLocation(state.location ?? null),
              locale: slimLocale(state.locale ?? null),
            };
          }
          if (version < 4) {
            // v4 step: no provenance was tracked before, so we can't claim the
            // cached coords are device-precise. Default to null → distance
            // hidden until re-capture.
            state = { ...state, locationSource: null };
          }
          if (version < 5) {
            // v5 step: no locale provenance tracked before. Default to null →
            // switcher shows the locale name only until the next typed switch.
            state = { ...state, localeSource: null, localeZip: null };
          }
          if (version < 6) {
            // v6 step: no fix timestamp tracked before. Null → maximally stale,
            // so an existing device user re-reads once on next app-open.
            state = { ...state, deviceFixAt: null };
          }
          if (version < 7) {
            // v7 step: fold the retired locale-provenance axis into the single
            // `locationSource`. A user mid-ZIP-switch recorded 'postal' on
            // localeSource while locationSource could still read 'device' — so
            // carry that ZIP choice over (distance + crosshair then hide), then
            // drop the now-defunct field.
            if (state.localeSource === 'postal' && state.locationSource !== 'postal') {
              state = { ...state, locationSource: 'postal' };
            }
            const { localeSource: _droppedLocaleSource, ...rest } = state;
            state = rest;
          }
        }
        return state ?? persistedState;
      },
      // Project to the slim shape at write time. In-memory state keeps the
      // fat Address/Locale so consumers see whatever the geocoder/BE
      // returned for the lifetime of the page; localStorage only ever
      // holds the slim subset.
      partialize: (state) => ({
        location: slimLocation(state.location),
        localeId: state.localeId,
        locale: slimLocale(state.locale),
        locationSource: state.locationSource,
        deviceFixAt: state.deviceFixAt,
        localeZip: state.localeZip,
      }),
      storage: createJSONStorage(() => localStorage),
    },
  ),
);

/** A location is sufficient to resolve a locale / load the feed when we have a country + postal code. */
export function isSufficientLocation(addr: Address | null | undefined): boolean {
  return !!(addr && addr.country && addr.postalCode);
}

/**
 * The user has a resolved locale — the feed is ready to render. Separate
 * predicate from `isSufficientLocation` (which asks "is THIS address
 * resolvable") so callers gating on "do I have an operating context" stay
 * decoupled from address shape. Used by HomePage to decide between
 * `InlineLocationPrompt` and the feed.
 */
export function hasLocale(locale: Locale | null | undefined): boolean {
  return !!locale;
}
